import { motion } from "framer-motion";
import { ParticleField, MorphBlob } from "@/components/effects/DynamicBackgrounds";
import { Heart, Users, Trophy, Globe, Target, Shield, Recycle, Dumbbell } from "lucide-react";
import { SEOHead } from "@/components/seo/SEOHead";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const PHOTOS = [
  {
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_eaa678b7-9af1-4aad-8a6a-f3c851cbd4dd/artifacts/m433zv3z_WhatsApp%20Image%202026-08-23%20at%202.59.55%20PM.jpeg",
    alt: "ProcureAide-sponsored women's rugby team at PARCO National 7s Rugby Championship 2026",
    caption: "PARCO 26th Men & 5th Women National 7's Rugby Championship 2026",
  },
  {
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_eaa678b7-9af1-4aad-8a6a-f3c851cbd4dd/artifacts/h6uasgrq_WhatsApp%20Image%202026-08-23%20at%202.59.22%20PM.jpeg",
    alt: "U19 Boys Rugby 7s Championship 2025 - Runner Up trophy presentation",
    caption: "U19 Boys Rugby 7's Championship 2025",
  },
  {
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_eaa678b7-9af1-4aad-8a6a-f3c851cbd4dd/artifacts/8lvoihxg_WhatsApp%20Image%202026-08-23%20at%203.01.35%20PM.jpeg",
    alt: "Youth rugby players proudly displaying ProcureAide-sponsored jerseys",
    caption: "Youth players with ProcureAide-sponsored kits",
  },
];

const INVESTMENTS = [
  { icon: Trophy, label: "Youth development", color: "#F59E0B" },
  { icon: Users, label: "Girls' participation and gender inclusion in sport", color: "#EC4899" },
  { icon: Shield, label: "Boys' development and positive pathways", color: "#3B82F6" },
  { icon: Heart, label: "Teamwork, inclusion and belonging", color: "#EF4444" },
  { icon: Dumbbell, label: "Physical health, wellbeing and confidence", color: "#10B981" },
  { icon: Target, label: "Leadership, discipline, resilience and life skills", color: "#8B5CF6" },
  { icon: Globe, label: "Grassroots sport and stronger communities", color: "#06B6D4" },
  { icon: Recycle, label: "Sustainable community impact", color: "#22C55E" },
];

const HASHTAGS = [
  "ProcureAide", "CorporateSocialResponsibility", "CSR", "SocialImpact", "CommunityImpact",
  "YouthEmpowerment", "YouthDevelopment", "CommunityDevelopment", "GivingBack", "Rugby",
  "YouthRugby", "GrassrootsRugby", "GirlsInRugby", "WomenInSport", "GenderEquality",
  "Inclusion", "DiversityAndInclusion", "DEI", "SportForDevelopment", "YouthSport",
  "Leadership", "Wellbeing", "Sustainability", "ResponsibleBusiness", "ESG",
  "CommunityEngagement", "FutureLeaders", "EmpoweringPotential",
];

export const CommunityPage = () => {
  return (
    <div className="bg-[#020617] pt-20" data-testid="community-page">
      <SEOHead
        title="Community"
        description="ProcureAide is proud to support youth girls' and boys' rugby, investing in leadership, inclusion and community development in Pakistan and Canada."
        path="/community"
      />
      {/* Hero */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-animated opacity-20" />
        <ParticleField count={15} />
        <MorphBlob color="rgba(0,180,216,0.06)" size="400px" className="top-[10%] right-[-10%]" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="text-center">
            <motion.p variants={fadeInUp} className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4">
              Community & Social Impact
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] tracking-tight mb-6">
              Proud Sponsor.{" "}
              <span className="text-[#00B4D8]">Proud to Give Back.</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-base sm:text-lg text-[#94A3B8] max-w-3xl mx-auto">
              We are proud to support youth girls' and boys' rugby, helping young athletes
              access the sport, build confidence and develop the values that will shape them
              far beyond the rugby field.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {PHOTOS.map((photo) => (
              <motion.div
                key={photo.caption}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="group"
              >
                <div className="rounded-xl overflow-hidden border border-[#00B4D8]/10 hover:border-[#00B4D8]/30 transition-all">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="bg-[#0B1426] p-4">
                    <p className="text-xs text-[#94A3B8] text-center">{photo.caption}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Invest In */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-2xl sm:text-3xl font-bold text-[#F8FAFC] text-center mb-4">
              What We Invest In
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#94A3B8] text-center mb-12 max-w-2xl mx-auto">
              By sponsoring young rugby players, we are investing in:
            </motion.p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {INVESTMENTS.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.label}
                    variants={fadeInUp}
                    className="bg-[#0B1426] border border-[#00B4D8]/10 rounded-xl p-5 hover:border-[#00B4D8]/25 transition-all"
                  >
                    <Icon className="w-6 h-6 mb-3" style={{ color: item.color }} />
                    <p className="text-sm text-[#E2E8F0] leading-relaxed">{item.label}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-[#0B1426]/50 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-8"
          >
            <motion.p variants={fadeInUp} className="text-base text-[#94A3B8] leading-relaxed">
              Sport has the power to develop more than athletes. It develops leaders,
              confidence, character and community.
            </motion.p>

            <motion.p variants={fadeInUp} className="text-base text-[#94A3B8] leading-relaxed">
              We are particularly proud to stand behind girls and boys equally, because access
              to opportunity, mentorship and development should never be determined by gender
              or background.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="border-l-4 border-[#00B4D8] pl-6 py-2"
            >
              <p className="text-lg text-[#F8FAFC] font-medium leading-relaxed">
                ProcureAide is more than a technology platform. We want to be a responsible
                business that creates value for our clients, our communities and future generations.
              </p>
            </motion.div>

            <motion.p variants={fadeInUp} className="text-base text-[#94A3B8] leading-relaxed">
              This is our contribution to a future where business success and social impact
              go hand in hand.
            </motion.p>

            <motion.p variants={fadeInUp} className="text-base text-[#E2E8F0] font-medium leading-relaxed">
              Because investing in young people is one of the most meaningful investments
              any business can make.
            </motion.p>

            <motion.p variants={fadeInUp} className="text-xl text-[#00B4D8] font-bold pt-4">
              Proud sponsor. Proud supporter. Proud to give back.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Hashtags */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {HASHTAGS.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full text-[10px] font-medium bg-[#0B1426] text-[#64748B] border border-[#00B4D8]/10"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CommunityPage;
